import React, {useEffect, useState} from 'react'
import {useStaticQuery, graphql} from 'gatsby'
import {css} from '@emotion/react'
import {logDplWithExtensionParams, EVENTS} from '../../../util/dpl'
import {isChrome} from '../../../util/userAgent'
import {logSgc, setExtensionParamsToLocalStorage} from '../../../util/extensionMetadata'
import {colors} from '../../../constants'
import {createMarkup, convertDatoCmsImgPathsToLocal} from '../../../util/datoCmsDataFactory'
import {chromeProtectionExtensionLink, firefoxProtectionExtensionLink} from '../../../util/extensionStoreLinks'
import Button from './button'
import SideBySide from '../../SideBySide'
import Footer from './Footer'

import StartpageLogoRedesign from '../../../images/startpage-logo-redesign.svg'

const CONTAINER_STYLES = `
    width: 1200px;
    margin: auto;
    padding: 0 1rem;
    h2 {
        font-size: 48px;
        font-weight: 600;
    }
`

const waveClipPath = `polygon(100% 0,0 0,0 77.5%,1% 77.4%,2% 77.1%,3% 76.6%,4% 75.9%,5% 75.05%,6% 74.05%,7% 72.95%,8% 71.75%,9% 70.55%,10% 69.3%,11% 68.05%,12% 66.9%,13% 65.8%,14% 64.8%,15% 64%,16% 63.35%,17% 62.85%,18% 62.6%,19% 62.5%,20% 62.65%,21% 63%,22% 63.5%,23% 64.2%,24% 65.1%,25% 66.1%,26% 67.2%,27% 68.4%,28% 69.65%,29% 70.9%,30% 72.15%,31% 73.3%,32% 74.35%,33% 75.3%,34% 76.1%,35% 76.75%,36% 77.2%,37% 77.45%,38% 77.5%,39% 77.3%,40% 76.95%,41% 76.4%,42% 75.65%,43% 74.75%,44% 73.75%,45% 72.6%,46% 71.4%,47% 70.15%,48% 68.9%,49% 67.7%,50% 66.55%,51% 65.5%,52% 64.55%,53% 63.75%,54% 63.15%,55% 62.75%,56% 62.55%,57% 62.5%,58% 62.7%,59% 63.1%,60% 63.7%,61% 64.45%,62% 65.4%,63% 66.45%,64% 67.6%,65% 68.8%,66% 70.05%,67% 71.3%,68% 72.5%,69% 73.6%,70% 74.65%,71% 75.55%,72% 76.35%,73% 76.9%,74% 77.3%,75% 77.5%,76% 77.45%,77% 77.25%,78% 76.8%,79% 76.2%,80% 75.4%,81% 74.45%,82% 73.4%,83% 72.25%,84% 71.05%,85% 69.8%,86% 68.55%,87% 67.35%,88% 66.2%,89% 65.2%,90% 64.3%,91% 63.55%,92% 63%,93% 62.65%,94% 62.5%,95% 62.55%,96% 62.8%,97% 63.3%,98% 63.9%,99% 64.75%,100% 65.7%)`

const ProtectionV1 = ({experiment, languageCode}) => {
    const site = useStaticQuery(graphql`
        query ProtectionV1 {
            site {
                siteMetadata {
                    ...ProtectionLanding
                }
            }
        }
    `)

    const {
        ctaText,
        landingSection1ExtensionText,
        landingSection1Title,
        landingSection1Subtitle,
        landingSection1Description,
        landingPreviewImage,
        landingSection2Title,
        landingSection2Pills,
        landingSection3Title,
        landingSection3SideBySide,
        landingSection4Title,
        landingEulaLink,
        newsTagline,
        newsLogos,
        footerBlogAsset,
        footerLinks,
    } = site.site.siteMetadata.privacyprotect[languageCode]

    const homeLink = `https://www.startpage.com/${languageCode}`

    const [hasFiredVisit, setHasFiredVisit] = useState(false)
    const [extensionText, setExtensionText] = useState(landingSection1ExtensionText)
    const [installLink, setInstallLink] = useState(firefoxProtectionExtensionLink(languageCode))

    const landingSection4Subtitle = {
        en: `Startpage Privacy Protection extension never passes a person's IP address or collects personal data. The extension abides by our <a href="https://www.startpage.com/en/privacy-policy" target="_blank" rel="noopener noreferrer nofollow">Privacy Policy</a>. Additional browser-specific information can be found <a href="${installLink}" target="_blank" rel="noopener noreferrer nofollow">here</a>.`,
        de: `Die Startpage Privatsphäre-Schutz Erweiterung gibt niemals die IP-Adresse einer Person weiter und sammelt keine persönliche Daten. Auch für die Erweiterung gilt unsere <a href="https://www.startpage.com/de/privacy-policy" target="_blank" rel="noopener noreferrer nofollow">Datenschutzrichtlinie</a>. Weiterführende browserspezifische Informationen gibt es <a href="${installLink}" target="_blank" rel="noopener noreferrer nofollow">hier</a>.`,
    }

    const setStoreLink = () => {
        if (isChrome()) {
            setInstallLink(chromeProtectionExtensionLink(languageCode))
        } else {
            // firefox
            setInstallLink(firefoxProtectionExtensionLink(languageCode))
        }
    }

    useEffect(() => {
        if (!hasFiredVisit) {
            setHasFiredVisit(true)
            setExtensionParamsToLocalStorage(experiment).then(() => {
                logDplWithExtensionParams(EVENTS.StartpagePageLoad, {
                    experiment,
                })
                logSgc()
            })
            if (isChrome()) {
                setExtensionText(landingSection1ExtensionText.replace(`{}`, `Chrome`))
            } else {
                setExtensionText(landingSection1ExtensionText.replace(`{}`, `Firefox`))
            }
            setStoreLink()
        }
    }, [hasFiredVisit, experiment, landingSection1ExtensionText, setStoreLink])

    return (
        <section
            css={css`
                h1,
                h2,
                h3,
                h4,
                p,
                b,
                strong {
                    color: ${colors.midnight};
                }
                position: relative;
                overflow-x: hidden;
            `}
        >
            <section
                className="header"
                css={css`
                    background: ${colors.whisper};
                    color: ${colors.midnight};
                `}
            >
                <div
                    css={css`
                        ${CONTAINER_STYLES}
                        padding: 3rem 1rem 3rem;
                        svg {
                            position: relative;
                            z-index: 1;
                        }
                    `}
                >
                    <a href={homeLink} rel="noopener noreferrer">
                        <StartpageLogoRedesign />
                    </a>
                </div>
            </section>

            <section className="section-1">
                <div
                    className="wave-mask"
                    css={css`
                        &::after {
                            clip-path: ${waveClipPath};
                            background: ${colors.whisper};
                            display: block;
                            height: 1080px;
                            position: absolute;
                            top: 0;
                            left: -2000px;
                            width: 380%;
                            content: '';
                            @media (max-width: 800px) {
                                width: 580%;
                            }
                        }
                    `}
                />
                <div
                    css={css`
                        ${CONTAINER_STYLES}
                        position: relative;
                        display: flex;
                        justify-content: space-between;
                        padding: 0 1rem 2rem;
                        h1 {
                            font-size: 48px;
                            font-weight: 600;
                            margin: 1rem 0 0;
                            color: ${colors.midnight};
                        }
                        .subtitle {
                            font-weight: 600;
                            color: ${colors.midnight};
                            margin-bottom: 2rem;
                            margin-top: 0.5rem;
                        }
                        .description {
                            p {
                                color: ${colors.midnight};
                                margin: 1rem 0 2rem;
                            }
                        }
                        button {
                            margin-bottom: 4rem;
                        }
                    `}
                >
                    <div
                        className="intro-info"
                        css={css`
                            max-width: 540px;
                            .extension-text {
                                color: ${colors.brandBlue};
                                font-weight: 600;
                                font-size: 20px;
                            }
                        `}
                    >
                        <p className="extension-text">{extensionText}</p>
                        <h1>{landingSection1Title}</h1>
                        <div className="subtitle" dangerouslySetInnerHTML={createMarkup(landingSection1Subtitle)} />
                        <div
                            className="description"
                            dangerouslySetInnerHTML={createMarkup(landingSection1Description)}
                        />
                        <Button text={ctaText} ctaType="cta-1" experiment={experiment} languageCode={languageCode} />
                        <div
                            className="trusted-by-section"
                            css={css`
                                display: flex;
                                align-items: center;
                                .tagline {
                                    color: ${colors.midnightMedium};
                                    margin: 0 1.75rem 0 0;
                                    ${languageCode === 'de' && 'flex: 0 0 25%;'}
                                }
                                img {
                                    margin: 0 1.5rem 0 0;
                                    max-width: 171px;
                                }
                            `}
                        >
                            <p className="tagline">{newsTagline}</p>
                            {newsLogos.map(({logo: {url = ``}}, i) => {
                                return <img key={i} src={convertDatoCmsImgPathsToLocal(url)} alt="news logo" />
                            })}
                        </div>
                    </div>
                    <div className="app-preview-image-container">
                        <img src={convertDatoCmsImgPathsToLocal(landingPreviewImage.url)} alt="extension preview" />
                    </div>
                </div>
            </section>

            <section
                className="section-2"
                css={css`
                    ${CONTAINER_STYLES}
                    padding: 6rem 0 8rem;
                    h2 {
                        font-size: 46px;
                        margin: 0 0 4rem;
                        text-align: center;
                    }
                `}
            >
                <h2>{landingSection2Title}</h2>
                <div
                    css={css`
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        grid-gap: 2rem;
                    `}
                >
                    {landingSection2Pills.map(({title, description, image: {url = ``}}, i) => {
                        return (
                            <div
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    padding: 2rem;
                                    border-radius: 4px;
                                    border: ${colors.whisper};
                                    box-shadow: 0px 10px 20px rgba(32, 41, 69, 0.15);
                                    img {
                                        margin-right: 1.5rem;
                                    }
                                    h3 {
                                        margin-bottom: 0.5rem;
                                    }
                                `}
                                key={i}
                            >
                                <img src={url} alt="privacy logo" />
                                <div>
                                    <h3>{title}</h3>
                                    <div dangerouslySetInnerHTML={createMarkup(description)} />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </section>

            <section
                className="section-3"
                css={css`
                    padding: 4rem 0 6rem;
                    background: ${colors.whisper};
                `}
            >
                <div
                    css={css`
                        ${CONTAINER_STYLES}
                        h2 {
                            margin: 0 0 2rem;
                            text-align: center;
                        }
                    `}
                >
                    <h2>{landingSection3Title}</h2>
                    <div>
                        {landingSection3SideBySide.map((item, i) => {
                            const {
                                title,
                                description,
                                image: {url = ``},
                            } = item
                            let overrideStyles = ``
                            switch (i) {
                                case 0:
                                    overrideStyles = `
                                            .side-by-side-text {
                                                margin: -3rem 0 0;
                                            }
                                        `
                                    break
                                case 1:
                                    overrideStyles = `
                                            .side-by-side-text {
                                                margin: -1rem 0 0;
                                            }
                                        `
                                    break
                                case 2:
                                    overrideStyles = `
                                            .side-by-side-text {
                                                margin: 1.5rem 0 0;
                                            }
                                        `
                                    break
                                case 3:
                                    overrideStyles = `
                                            .side-by-side-text {
                                                margin: 2rem 0 0;
                                            }
                                        `
                                    break
                                default:
                                    overrideStyles = `
                                            .side-by-side-text {
                                                margin: -3rem 0 0;
                                            }
                                        `
                                    break
                            }
                            return (
                                <div key={i}>
                                    <SideBySide
                                        imageSrc={convertDatoCmsImgPathsToLocal(url)}
                                        title={title}
                                        description={description}
                                        imagePosition={i % 2 === 0 ? 'left' : 'right'}
                                        overrideStyles={overrideStyles}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            <section
                className="section-4"
                css={css`
                    ${CONTAINER_STYLES}
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 4rem 1rem;
                    h2 {
                        margin: 0 0 2rem;
                        max-width: 48rem;
                        text-align: center;
                    }
                `}
            >
                {/* these divs are like a confeti texture in the bg */}
                <div
                    css={css`
                        height: 40px;
                        width: 40px;
                        border: 2px solid ${colors.brandBlueLight};
                        position: absolute;
                        left: -1rem;
                        top: 5rem;
                    `}
                />
                <div
                    css={css`
                        height: 64px;
                        width: 64px;
                        border: 2px solid ${colors.whisperDark};
                        position: absolute;
                        opacity: 0.5;
                        left: 0;
                        top: 6rem;
                    `}
                />
                <div
                    css={css`
                        height: 64px;
                        width: 64px;
                        border: 2px solid ${colors.brandBlueLight};
                        position: absolute;
                        top: 9rem;
                        right: 4rem;
                    `}
                />
                <h2>{landingSection4Title}</h2>
                <div
                    dangerouslySetInnerHTML={createMarkup(landingSection4Subtitle[languageCode])}
                    css={css`
                        text-align: center;
                        margin: 0 0 2rem;
                        max-width: ${languageCode === 'de' ? '731px' : '776px'};
                        line-height: 25px;
                        font-size: 16px;
                        a {
                            color: ${colors.brandBlue};
                            text-decoration: underline;
                        }
                    `}
                />
                <Button text={ctaText} ctaType="cta-2" experiment={experiment} languageCode={languageCode} />
            </section>
            <Footer
                languageCode={languageCode}
                links={footerLinks}
                footerBlogAsset={footerBlogAsset}
                additionalLinks={landingEulaLink}
            />
        </section>
    )
}

export default ProtectionV1
